import React, { useMemo } from "react";
import HistogramPlot from "./HistogramPlot";
import ScatterPlot from "./ScatterPlot";
import PlotGrid from "./PlotGrid";

export interface NormalizationPlotsProps {
  data: any[];
  className?: string;
}

// try multiple possible column names (supports different backend schemas)
const pickFirstExistingKey = (row: any, candidates: string[]) => {
  if (!row) return undefined;
  return candidates.find((k) => row[k] != null);
};

const NormalizationPlots = ({
  data,
  className = "",
}: NormalizationPlotsProps): React.ReactElement => {
  const rows = React.useMemo(
  () => (Array.isArray(data) ? data : []),
  [data],
);


  const { beforeKey, afterKey } = useMemo(() => {
    const sample = rows.find((r) => r && typeof r === "object");

    // Add candidates that match your backend possibilities
    const beforeCandidates = [
      "Total Counts Before",
      "total_counts_before",
      "before_total_counts",
      "total_counts_raw",
      "raw_total_counts",
      "total_counts", // fallback (sometimes only one exists)
    ];

    const afterCandidates = [
      "Total Counts After",
      "total_counts_after",
      "after_total_counts",
      "total_counts_norm",
      "normalized_total_counts",
      "log1p_total_counts",
    ];

    return {
      beforeKey: pickFirstExistingKey(sample, beforeCandidates),
      afterKey: pickFirstExistingKey(sample, afterCandidates),
    };
  }, [rows]);

  if (!rows.length) {
    return <p>No normalization data available.</p>;
  }

  // If we can't find required columns, don't render plots (prevents Plotly crash)
  if (!beforeKey || !afterKey) {
    // Helpful debug message (remove later if you want)
    const sampleKeys = rows[0] ? Object.keys(rows[0]) : [];
    return (
      <div>
        <p>No normalization plot columns found.</p>
        <p style={{ fontSize: 12, color: "#6b7280" }}>
          Expected columns like: total_counts_before / total_counts_after (or your backend names).
          <br />
          First row keys: {sampleKeys.slice(0, 20).join(", ")}
        </p>
      </div>
    );
  }

  return (
    <PlotGrid className={className}>
      <HistogramPlot data={rows} metric={beforeKey} title="Histogram: Total Counts Before" />
      <HistogramPlot data={rows} metric={afterKey} title="Histogram: Total Counts After" />
      <ScatterPlot
        data={rows}
        xMetric={beforeKey}
        yMetric={afterKey}
        title="Before vs After Normalization"
      />
    </PlotGrid>
  );
};

export default NormalizationPlots;
